import { Component, inject, signal, computed, input } from '@angular/core';
import { Router } from '@angular/router';
import { environment } from '../../../../environments/environment';
import { UserService } from '../../../services';
import { RouterOutlet } from '@angular/router';
import { jwtDecode } from 'jwt-decode';
import { TransfloTopNavComponent } from '../../transflo-top-nav/transflo-top-nav.component';
import { ConnectedService } from '../../../services/connected/connected.service';
import { ConfirmationService, ConfirmEventType } from 'primeng/api';
import { ConfirmDialogModule } from 'primeng/confirmdialog';

@Component({
  selector: 'app-page-layout',
  standalone: true,
  imports: [RouterOutlet, TransfloTopNavComponent, ConfirmDialogModule],
  providers: [ConfirmationService],
  templateUrl: './page-layout.component.html',
  styleUrl: './page-layout.component.scss',
})
export class PageLayoutComponent {
  //url params
  public localAccess = input<string>();
  public localRefresh = input<string>();

  router = inject(Router);
  userService = inject(UserService);
  connectedService = inject(ConnectedService);
  confirmationService = inject(ConfirmationService);

  baseUrl = signal(location.origin);
  portalApiUrl = signal(environment.portalApiUrl || 'portalURlNotFound');
  portalSiteUrl = signal(environment.portalSiteUrl || 'portalSiteURlNotFound');

  isLocal = signal(window.location.hostname.includes('localhost'));
  localTokens = computed(() => {
    const access = this.localAccess();
    const refresh = this.localRefresh();
    if (access === undefined || refresh === undefined) return undefined;
    return { accessToken: access, refreshToken: refresh };
  });

  isTokenLoaded = signal(false);

  /**
   * Shared by both dialogs below. Extracted so a test can assert that Escape does *not*
   * navigate without the assertion itself unloading the page.
   */
  navigateToPortal = () => {
    window.location.href = this.portalSiteUrl();
  };

  handleTokenIngesting = (
    tokens: { accessToken: string; refreshToken: string } | undefined
  ) => {
    if (!tokens) return;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const claims: any = jwtDecode(tokens.accessToken);

    // Log the claims don't trust anything
    // console.log(claims);

    // Set the user first, so isGlobalAdmin is resolvable in the check below.
    this.userService.setUser({
      customerId: claims.customerId,
      userId: claims.userId,
      firstName: claims.firstName,
      lastName: claims.lastName,
      isImpersonating:
        claims.isImpersonating === true || claims.isImpersonating === 'true',
      userType: claims.userType,
      portalRole: claims.portalRole,
      applicationRoles: claims.applicationRoles,
    });

    // A Transflo super-admin reaches Studio without impersonating and works only on global
    // templates, so having no customer is expected. Everyone else still needs one.
    // isGlobalAdmin already requires an absent customerId, so the two branches are exclusive.
    if (!claims.customerId) {
      if (this.userService.isGlobalAdmin()) {
        this.confirmationService.confirm({
          header: 'Global template workspace',
          message:
            'You are not working inside a specific customer. Anything you save here becomes a ' +
            'global template available to every customer. To work on one customer’s ' +
            'workflows, return to the portal and impersonate that customer.',
          acceptLabel: 'Continue',
          rejectLabel: 'Return to Portal',
          accept: () => undefined,
          // PrimeNG also calls reject() for Escape, a mask click and the close icon, passing
          // ConfirmEventType.CANCEL. Only a real Return to Portal click may navigate away.
          reject: (type: ConfirmEventType) => {
            if (type === ConfirmEventType.REJECT) {
              this.navigateToPortal();
            }
          },
        });
      } else {
        this.confirmationService.confirm({
          header: 'Must impersonate to access studio',
          message: 'Return to portal and impersonate',
          acceptLabel: 'Return to Portal',
          rejectVisible: false,

          accept: () => {
            this.navigateToPortal();
          },
        });
      }
    }

    localStorage.setItem('accessToken', tokens.accessToken ?? '');
    this.isTokenLoaded.set(true);
  };

  handleLogout = () => {
    localStorage.removeItem('accessToken');
    this.userService.setUser(null);
    this.connectedService.isLoggedIn.next(false);
  };
}
